import { Badge } from '@mantine/core';
import { IconEyeOff, IconLock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import classes from './CardMetaBadge.module.css';

interface VisibilityBadgeProps {
  /** Renders ONLY when this is exactly `false` — public products get no badge (exception-based). */
  isPublic?: boolean;
  linkShareMode?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showIcon?: boolean;
  style?: React.CSSProperties;
  appearance?: 'default' | 'card';
  /** Card grids: icon-only chip (full text in tooltip) so the meta row never overflows. */
  iconOnly?: boolean;
}

/**
 * Owner-facing "not public" indicator (dashboard only — never render on public surfaces).
 * Same visual language as the ProductViewer owner banner (eye-off, amber).
 */
export function VisibilityBadge({
  isPublic,
  linkShareMode,
  size = 'sm',
  showIcon = true,
  style,
  appearance = 'default',
  iconOnly = false,
}: VisibilityBadgeProps) {
  const { t } = useTranslation();

  if (isPublic !== false) return null;

  const modeLabel =
    linkShareMode === 'password'
      ? t('editProduct.linkShare.modePassword')
      : linkShareMode === 'restricted'
        ? t('editProduct.linkShare.modeRestricted')
        : t('editProduct.linkShare.modeOpen');
  const label = t('dashboard.visibility.notPublic');
  const title = `${t('productViewer.ownerBar.notPublic')} ${t('productViewer.ownerBar.linkAccess', { mode: modeLabel })}`;
  const Icon = linkShareMode === 'password' ? IconLock : IconEyeOff;

  if (appearance === 'card') {
    if (iconOnly) {
      return (
        <span
          className={`${classes.badge} ${classes.notPublic} ${classes.iconOnly}`}
          style={style}
          title={title}
          aria-label={label}
        >
          <Icon size={12} className={classes.icon} stroke={2} />
        </span>
      );
    }
    return (
      <span className={`${classes.badge} ${classes.notPublic}`} style={style} title={title}>
        {showIcon && <Icon size={9} className={classes.icon} stroke={2} />}
        <span className={classes.label}>{label}</span>
      </span>
    );
  }

  const iconSize = size === 'lg' ? 14 : size === 'sm' ? 12 : 10;
  const gap = size === 'lg' ? '4px' : '3px';

  return (
    <Badge color="yellow" variant="light" size={size} style={style} title={title}>
      <div style={{ display: 'flex', alignItems: 'center', gap }}>
        {showIcon && <Icon size={iconSize} />}
        {label}
      </div>
    </Badge>
  );
}
